import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../components/sidebar-filters/useSavedFiltersList';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../domain/saved-filters/queries';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { usePageLocation } from '../../usePageState';
import {
    checkFilterInvalidValues,
    convertSitesPageStateToFilterState,
    countNotEmptyFilters,
    transformSitesSavedFilterValuesFromDto,
    transformSitesSavedFilterValuesToDto,
} from './utils';

export interface ISitesPageState {
    filter?: {
        // Filters
        control_provider_id?: number | number[];
        utility_customer_id?: number | number[];
        program_id?: number | number[];
        site_utility?: string | string[];
        status?: string | string[];
        market?: string | string[];

        // Options
        dispatch_type?: string;
        hasVen?: string;
        resource_telemetry_status?: string;
        interval_data?: string;
        exist_salesforce_meter_enrollments?: string;
        exist_salesforce_meters?: string;
        isClosed?: string;
    };

    // Used by whole page
    companyId?: number;
    search?: string;

    // Used by table
    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface ISitesSidebarFilters {
    control_provider_id?: number[];
    utility_customer_id?: number[];
    program_id?: number[];
    site_utility?: string[];
    market?: string[];
    status?: string[];

    dispatch_type?: string | null;
    hasVen?: string | null;
    resource_telemetry_status?: string | null;
    interval_data?: string | null;
    exist_salesforce_meter_enrollments?: string | null;
    exist_salesforce_meters?: string | null;
    isClosed?: string | null;
}

export interface ISitesSidebarFiltersContext {
    siteFiltersCount: number;
    siteFilters: ISitesSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleSiteFilterChange: (key: string, value: unknown) => void;
    handleSiteFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null; // Using for create/edit saved filter actions
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const SitesSidebarFiltersContext = React.createContext<ISitesSidebarFiltersContext>({} as ISitesSidebarFiltersContext);

export const SitesSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    // Site Filtering
    const { siteFiltersCount, siteFilters, handleSiteFilterChange, handleSiteFiltersChange, handleSiteFiltersClear } = useSidebarFiltering();

    // Saving Site Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useSitesSavedFilters();

    const onSiteFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleSiteFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when site filters change
        },
        [handleApplySavedFilter, handleSiteFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleSiteFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleSiteFiltersChange]
    );

    const onSiteFiltersClear = useCallback(() => {
        handleSiteFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleSiteFiltersClear]);

    return (
        <SitesSidebarFiltersContext.Provider
            value={{
                siteFiltersCount,
                siteFilters,
                savedFilters,

                isSavedFiltersLoading,

                handleSiteFilterChange: onSiteFiltersChange,
                handleSiteFiltersClear: onSiteFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </SitesSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: ISitesPageState = queryToState(location.search);

    const [siteFilters, setSiteFilters] = useState<ISitesSidebarFilters>(convertSitesPageStateToFilterState(pageState));
    const siteFiltersCount = useMemo(() => countNotEmptyFilters(siteFilters), [siteFilters]);

    const handleSiteFilterChange = (key: string, value: unknown) => {
        setSiteFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: 1, // Reset to first page on filter change
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleSiteFiltersChange = (newFilters: ISitesSidebarFilters) => {
        setSiteFilters(newFilters);

        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: 1, // Reset to first page on filter change
            },
        });
    };

    const handleSiteFiltersClear = () => {
        setSiteFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        siteFiltersCount,
        siteFilters,

        handleSiteFilterChange,
        handleSiteFiltersChange,
        handleSiteFiltersClear,
    };
};

const useSitesSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.sites);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformSitesSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map(filter => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformSitesSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformSitesSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useSitesSidebarFiltersContext = () => {
    const context = React.useContext(SitesSidebarFiltersContext);
    if (!context) {
        throw new Error('useSitesSidebarFiltersContext must be used within SitesSidebarFiltersContext');
    }

    return context;
};
